/**
 * demoState.ts — Single Canonical Demo Bootstrap
 *
 * One resolver. One answer. No split-brain between demoDetection and demoPartner.
 *
 * Resolution precedence (highest to lowest):
 *   1. URL with explicit variant  → trust it, persist it, it wins
 *        /demo/pharmacy           → pharmacy
 *        /demo                    → consumer
 *        ?demo=1&partner=pharmacy → pharmacy
 *   2. URL with unspecified demo  → fall back to stored variant
 *        ?demo=1 (no partner)     → stored variant if present, else consumer
 *   3. sessionStorage             → used when URL carries no demo signal
 *   4. Default                    → { isDemo: false, variant: null }
 *
 * Why this matters:
 *   React Router SPA navigation drops query params like partner=pharmacy on
 *   every internal navigate() call. Treating ?demo=1 as "unspecified" (not
 *   "consumer") prevents SPA navigation from overwriting a stored pharmacy
 *   variant with the consumer default.
 *
 *   Explicit URLs (/demo/pharmacy, ?partner=pharmacy) always override stored
 *   state — so switching between demos in the same session still works cleanly.
 *
 * Place at: src/demo/demoState.ts
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type DemoVariant = 'consumer' | 'pharmacy' | null;

export interface ParsedDemoUrl {
  isDemo: boolean;
  explicitVariant: 'consumer' | 'pharmacy' | null;
}

export interface DemoState {
  isDemo: boolean;
  variant: DemoVariant;
}

// ── Storage ───────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'coretrak.demo.state';

// ── Module cache ──────────────────────────────────────────────────────────────

let _demoState: DemoState | undefined;

// ── URL resolver ──────────────────────────────────────────────────────────────

function parseDemoStateFromUrl(url: URL): ParsedDemoUrl {
  const { pathname, searchParams } = url;

  // Named router-owned routes — explicit pharmacy
  if (pathname === '/demo/pharmacy' || pathname === '/demo/pharmacy/') {
    return { isDemo: true, explicitVariant: 'pharmacy' };
  }

  // Named router-owned route — explicit consumer
  if (pathname === '/demo' || pathname === '/demo/') {
    return { isDemo: true, explicitVariant: 'consumer' };
  }

  // Query param entry (post-redirect)
  const demo = searchParams.get('demo');
  const partner = searchParams.get('partner');

  if (demo === '1' && partner === 'pharmacy') {
    return { isDemo: true, explicitVariant: 'pharmacy' };
  }

  // ?demo=1 alone — demo enabled but variant unspecified
  if (demo === '1') {
    return { isDemo: true, explicitVariant: null };
  }

  return { isDemo: false, explicitVariant: null };
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

/**
 * Called once at boot. Resolves demo state from URL, writes to session storage,
 * and caches in module scope. URL always takes precedence over session —
 * this prevents pharmacy bleeding into consumer and vice versa.
 */
export function initDemoState(): DemoState {
  if (typeof window === 'undefined') {
    _demoState = { isDemo: false, variant: null };
    return _demoState;
  }

  const url = new URL(window.location.href);
  const fromUrl = parseDemoStateFromUrl(url);

  // Not in demo mode — clear state
  if (!fromUrl.isDemo) {
    _demoState = { isDemo: false, variant: null };
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return _demoState;
  }

  // URL explicitly declares a variant — it wins, persist it
  if (fromUrl.explicitVariant) {
    _demoState = { isDemo: true, variant: fromUrl.explicitVariant };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_demoState));
    } catch { /* ignore */ }
    return _demoState;
  }

  // Demo enabled but variant unspecified (?demo=1 alone) —
  // fall back to stored variant so SPA navigation does not lose pharmacy context
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as DemoState;
      if (parsed && parsed.isDemo && parsed.variant) {
        _demoState = parsed;
        return _demoState;
      }
    }
  } catch { /* ignore */ }

  // No stored variant — default to consumer
  _demoState = { isDemo: true, variant: 'consumer' };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_demoState));
  } catch { /* ignore */ }
  return _demoState;
}

// ── Reads ─────────────────────────────────────────────────────────────────────

export function getDemoState(): DemoState {
  if (_demoState === undefined) return initDemoState();
  return _demoState;
}

export function isDemoMode(): boolean {
  return getDemoState().isDemo;
}

export function getDemoVariant(): DemoVariant {
  return getDemoState().variant;
}

export function isPharmacyDemo(): boolean {
  return getDemoState().variant === 'pharmacy';
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

/**
 * Clears demo state entirely. Use for "Exit demo" action.
 */
export function exitDemoState(): void {
  _demoState = { isDemo: false, variant: null };
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

/**
 * Resets module cache so next call to initDemoState() re-reads from URL/session.
 * Use for testing only.
 */
export function resetDemoState(): void {
  _demoState = undefined;
}

/**
 * Tiny future-proofing helper — pick demo or live data source.
 */
export function demoOr<T>(getDemo: () => T, getLive: () => T): T {
  return isDemoMode() ? getDemo() : getLive();
}
